// Considered: MeasureMembersListMolecule — rejected because it is the parent-side list of every member with unlink;
// the sheet is one edge seen from the member side with the parent read-only. See spec Reuse Audit.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { formatWeight } from '../RelatedSpheresEditor/formatWeight';
import DrawerAtom from '../../atoms/Drawer/DrawerAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChoiceCardGroupAtom from '../../atoms/ChoiceCardGroup/ChoiceCardGroupAtom';
import ConfirmDialogAtom from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import InputNumberAtom from '../../atoms/InputNumber/InputNumberAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import StatusBadgeAtom from '../../atoms/status-badge/StatusBadgeAtom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useRollupExplanation } from '../FactorSummary/useRollupExplanation';
import { buildWhySentence } from '../FactorSummary/buildWhySentence';
import MeasureAddSheet, { type MeasureAddSheetProps } from './MeasureAddSheet';
import { useMeasureFactors } from './useMeasureFactors';
import { measureFactorChoices } from './measureFactorChoices';
import './MeasureSheetMolecule.scss';

export interface MeasureViewSheetProps {
  visible: boolean;
  onHide: () => void;
  /** The measure the sphere rolls up into. */
  parentId: string;
  /** The parent's rollup factor this sphere is in. Absent when the row is ambiguous: the sheet then asks which one. */
  factorId?: string;
  /** The sphere whose edge is shown: the member side. */
  memberId: string;
  measureName: string;
  measureKind?: string;
  /** The edge's current weight; the route's default is 1. */
  weight?: number;
  /** Whether the caller holds `edit` on the measure (the read model's capability flag). */
  editable: boolean;
  /** Called after the weight is saved, so the card can reload. */
  onSaved?: () => void;
}

const DEFAULT_WEIGHT = 1;

const failureReason = (err: unknown): string => {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message ?? e?.message ?? '';
};

const MeasureViewSheet: React.FC<MeasureViewSheetProps> = ({
  visible,
  onHide,
  parentId,
  factorId: givenFactorId,
  memberId,
  measureName,
  measureKind,
  weight,
  editable,
  onSaved,
}) => {
  const { t, i18n } = useTranslation();
  const graphClient = useApiClientsOrNull()?.graphClient;
  const logAnalyticsEvent = useAnalytics();
  const asksFactor = !givenFactorId;
  const {
    factors,
    loading: factorsLoading,
    error: factorsError,
    forbidden: factorsForbidden,
    reload: reloadFactors,
  } = useMeasureFactors(visible && asksFactor ? parentId : null);
  const [chosenFactorId, setChosenFactorId] = useState<string | null>(null);
  const factorId = givenFactorId ?? chosenFactorId;
  const { data, error, reload } = useRollupExplanation(visible && factorId ? parentId : null, visible ? factorId : null);

  const [savedWeight, setSavedWeight] = useState<number>(weight ?? DEFAULT_WEIGHT);
  const [draft, setDraft] = useState<number | null>(weight ?? DEFAULT_WEIGHT);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const loaded = Boolean(factorId && data?.explanation);
  const dirty = draft !== savedWeight;
  const invalid = draft === null || draft < 0;
  const showInvalid = invalid && touched;
  const canEdit = editable && loaded;
  const equalWeights = data?.explanation?.weighting === 'equal';
  const sentence = data?.explanation ? buildWhySentence(data.explanation, t) : '';

  useEffect(() => {
    if (factors.length === 1) setChosenFactorId(factors[0].id);
  }, [factors]);

  /** Another rollup is another edge: an unsaved weight or a save result belongs to the one it was typed against. */
  const chooseFactor = (id: string) => {
    setChosenFactorId(id);
    setDraft(savedWeight);
    setTouched(false);
    setSaveError(null);
    setJustSaved(false);
  };

  const handleChange = (value: number | null) => {
    setDraft(value);
    setJustSaved(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!graphClient || !factorId || invalid) return;
    setSaving(true);
    setSaveError(null);
    try {
      await graphClient.patch(`/spheres/${parentId}/factors/${factorId}/members/${memberId}`, { influence: draft });
      logAnalyticsEvent('rollup_upward_weight_changed', undefined, { from: savedWeight, to: draft });
      setSavedWeight(draft);
      setJustSaved(true);
      onSaved?.();
    } catch (err) {
      setSaveError(failureReason(err));
    } finally {
      setSaving(false);
    }
  };

  const requestHide = () => {
    if (dirty) setConfirmDiscard(true);
    else onHide();
  };

  const discard = () => {
    setConfirmDiscard(false);
    setDraft(savedWeight);
    setTouched(false);
    onHide();
  };

  /** The ambiguous row's step before the measure can be read: which of the parent's rollups to show. */
  const renderFactorChoice = () => {
    if (factorsForbidden) {
      return <p className="measure-sheet__reason">{t('statusFactors.upward.sheet.cannotView', { measure: measureName })}</p>;
    }
    if (factorsError) {
      return (
        <div className="measure-sheet__error">
          <MessageAtom severity="error" text={t('statusFactors.upward.sheet.loadError', { measure: measureName })} />
          <ButtonAtom variant="text" size="small" label={t('statusFactors.upward.retry')} onClick={reloadFactors} />
        </div>
      );
    }
    if (factorsLoading) {
      return (
        <div className="measure-sheet__loading" aria-busy="true">
          <SkeletonAtom height="var(--font-size-md)" count={2} />
        </div>
      );
    }
    if (factors.length === 0) {
      return <p className="measure-sheet__reason">{t('statusFactors.upward.sheet.noFactor', { measure: measureName })}</p>;
    }
    if (factors.length === 1) return null;
    return (
      <>
        {!factorId && <p className="measure-sheet__reason">{t('statusFactors.upward.sheet.chooseFactor', { measure: measureName })}</p>}
        <ChoiceCardGroupAtom
          ariaLabel={t('statusFactors.upward.sheet.factorLabel', { measure: measureName })}
          value={factorId}
          onChange={chooseFactor}
          options={measureFactorChoices(factors, t, i18n.language)}
        />
      </>
    );
  };

  const renderMeasure = () => {
    if (!factorId) return null;
    if (error && !data) {
      return (
        <div className="measure-sheet__error">
          <MessageAtom
            severity="error"
            text={t('statusFactors.upward.sheet.loadError', { measure: measureName })}
          />
          <ButtonAtom variant="text" size="small" label={t('statusFactors.upward.retry')} onClick={reload} />
        </div>
      );
    }
    if (!data || !loaded) {
      return (
        <div className="measure-sheet__loading" aria-busy="true">
          <SkeletonAtom height="var(--font-size-md)" count={2} />
        </div>
      );
    }
    return (
      <>
        <div className="measure-sheet__measure-line">
          <span className="measure-sheet__name">{measureName}</span>
          {measureKind && <span className="measure-sheet__kind">{measureKind}</span>}
          <StatusBadgeAtom status={data.status} variant="chip" showLabel />
        </div>
        {sentence && <p className="measure-sheet__sentence">{sentence}</p>}
      </>
    );
  };

  const weightInputId = `measure-sheet-weight-${memberId}`;
  const reasonId = `${weightInputId}-reason`;

  return (
    <>
      <DrawerAtom visible={visible} onHide={requestHide} position="right" width="28rem" header={<span className="measure-sheet__title">{measureName}</span>} className="measure-sheet">
        <div className="measure-sheet__body">
          <MessageAtom
            severity="warn"
            text={t(editable ? 'statusFactors.upward.sheet.warning' : 'statusFactors.upward.sheet.warningReadOnly')}
          />
          <section className="measure-sheet__measure" aria-label={measureName}>
            {asksFactor && renderFactorChoice()}
            {renderMeasure()}
          </section>
          <section className="measure-sheet__weight">
            {editable ? (
              <>
                <label className="measure-sheet__label" htmlFor={weightInputId}>
                  {t('statusFactors.upward.sheet.weightLabel')}
                </label>
                <div onBlur={() => setTouched(true)}>
                  <InputNumberAtom
                    inputId={weightInputId}
                    value={draft}
                    onChange={handleChange}
                    min={0}
                    precision={2}
                    step={0.1}
                    disabled={!canEdit}
                    invalid={showInvalid}
                    ariaDescribedBy={reasonId}
                  />
                </div>
              </>
            ) : (
              <dl className="measure-sheet__readonly">
                <dt className="measure-sheet__label">{t('statusFactors.upward.sheet.weightLabel')}</dt>
                <dd className="measure-sheet__value">{formatWeight(savedWeight, i18n.language)}</dd>
              </dl>
            )}
            <div id={reasonId} className="measure-sheet__reason">
              {showInvalid && (
                <p className="measure-sheet__field-error" role="alert">
                  {t('statusFactors.upward.sheet.invalidWeight')}
                </p>
              )}
              {!editable && (
                <p>
                  {t('statusFactors.upward.sheet.readOnly', {
                    measure: measureName,
                  })}
                </p>
              )}
              {loaded && equalWeights && (
                <p>
                  {t(
                    'statusFactors.upward.sheet.equalWeights',
                    { measure: measureName }
                  )}
                </p>
              )}
              {justSaved && !dirty && <output className="measure-sheet__saved">{t('statusFactors.upward.sheet.saved')}</output>}
            </div>
            {saveError !== null && (
              <MessageAtom
                severity="error"
                text={t(
                  'statusFactors.upward.sheet.saveError',
                  { reason: saveError || t('statusFactors.upward.sheet.unknownReason'), weight: formatWeight(savedWeight, i18n.language) }
                )}
              />
            )}
            {editable && (
              <ButtonAtom
                label={t('statusFactors.upward.sheet.save')}
                onClick={handleSave}
                disabled={!canEdit || !dirty || invalid || saving}
                loading={saving}
              />
            )}
            {editable && !dirty && !invalid && (
              <p className="measure-sheet__hint">{t('statusFactors.upward.sheet.saveHint')}</p>
            )}
          </section>
        </div>
      </DrawerAtom>
      <ConfirmDialogAtom
        visible={confirmDiscard}
        onHide={() => setConfirmDiscard(false)}
        header={t('statusFactors.upward.sheet.discardHeader')}
        message={t('statusFactors.upward.sheet.discardMessage', {
          weight: formatWeight(draft, i18n.language),
          saved: formatWeight(savedWeight, i18n.language),
        })}
        acceptLabel={t('statusFactors.upward.sheet.discard')}
        rejectLabel={t('statusFactors.upward.sheet.keepEditing')}
        accept={discard}
        variant="warning"
      />
    </>
  );
};

export type MeasureSheetMoleculeProps = (MeasureViewSheetProps & { mode?: 'view' }) | (MeasureAddSheetProps & { mode: 'add' });

/** One sheet, two readings of the edge: `view` an existing one, `add` a new one. */
const MeasureSheetMolecule: React.FC<MeasureSheetMoleculeProps> = (props) =>
  props.mode === 'add' ? <MeasureAddSheet {...props} /> : <MeasureViewSheet {...props} />;

export default MeasureSheetMolecule;
